import React, { useState, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import PrinterStatusIndicator from './PrinterStatusIndicator';
import logo from '../assets/logo.png';

const SIDEBAR_WIDTH = 256;
const SIDEBAR_COLLAPSED = 72;

// Menú agrupado por secciones; el orden aquí es el orden visual.
const MENU_SECTIONS = [
    {
        title: 'Operación',
        items: [
            { path: '/', icon: 'bi-house-door', label: 'Inicio', exact: true },
            { path: '/fast-food/pos', icon: 'bi-basket2', label: 'Punto de Venta' },
            { path: '/fast-food/tables', icon: 'bi-grid-3x3-gap', label: 'Mesas' },
            { path: '/fast-food/orders', icon: 'bi-receipt', label: 'Órdenes' },
            { path: '/fast-food/shift', icon: 'bi-cash-coin', label: 'Caja' },
            { path: '/fast-food/customers', icon: 'bi-person-badge', label: 'Clientes' },
        ],
    },
    {
        title: 'Carta',
        items: [
            { path: '/fast-food/inventory', icon: 'bi-journal-text', label: 'Menú' },
            { path: '/fast-food/variables', icon: 'bi-sliders', label: 'Variables' },
            { path: '/fast-food/promotions', icon: 'bi-gift', label: 'Promociones' },
            { path: '/fast-food/bodega', icon: 'bi-archive', label: 'Bodega (Insumos)' },
        ],
    },
    {
        title: 'Finanzas',
        items: [
            { path: '/fast-food/gastos', icon: 'bi-wallet2', label: 'Gastos' },
            { path: '/fast-food/tasa-cambio', icon: 'bi-currency-exchange', label: 'Tasa de Cambio' },
            { path: '/fast-food/facturacion-sri', icon: 'bi-file-earmark-check', label: 'Facturación SRI' },
            { path: '/fast-food/reports', icon: 'bi-graph-up', label: 'Reportes' },
        ],
    },
    {
        title: 'Sistema',
        items: [
            { path: '/fast-food/printers', icon: 'bi-printer', label: 'Impresoras' },
            { path: '/fast-food/secuencia', icon: 'bi-123', label: 'Turno / N° Orden' },
            { path: '/cobranzas', icon: 'bi-cash-stack', label: 'Cobranzas' },
            { path: '/users', icon: 'bi-people', label: 'Usuarios' },
        ],
    },
];

const EMPLOYEE_PATHS = ['/fast-food/pos', '/fast-food/tables', '/fast-food/orders', '/fast-food/inventory', '/fast-food/shift'];
const COOK_PATHS = [...EMPLOYEE_PATHS, '/fast-food/feria-shift'];

const BarraLateral = () => {
    const location = useLocation();
    const { user, logout } = useContext(AuthContext);
    const [isCollapsed, setIsCollapsed] = useState(false);

    const roleName = user?.role_details?.name;
    const isSuperAdmin = roleName === 'SUPER_ADMIN' || user?.is_superuser;
    const isGestorCobranzas = roleName === 'GESTOR_COBRANZAS';
    const isAdminFastFood = roleName === 'ADMIN_FAST_FOOD';
    const isCook = roleName === 'COOK' || user?.role === 'COOK' || user?.role_details?.description?.toLowerCase()?.includes('cocinero');

    const canSee = (item) => {
        // El Gestor de Cobranzas solo ve su panel; nadie más lo ve.
        if (isGestorCobranzas) return item.path === '/cobranzas';
        if (item.path === '/cobranzas') return false;
        if (isSuperAdmin) return true;
        if (isAdminFastFood && item.path.startsWith('/fast-food')) return true;
        if (isCook) return COOK_PATHS.includes(item.path);
        return EMPLOYEE_PATHS.includes(item.path);
    };

    const isActive = (item) => {
        if (item.exact) return location.pathname === item.path;
        return location.pathname === item.path || location.pathname.startsWith(item.path + '/');
    };

    const sections = MENU_SECTIONS
        .map((s) => ({ ...s, items: s.items.filter(canSee) }))
        .filter((s) => s.items.length > 0);

    const displayName = user?.first_name
        ? `${user.first_name} ${user.last_name || ''}`.trim()
        : (user?.username || 'Usuario');
    const roleLabel = user?.role_details?.description || user?.role_details?.name || user?.role || 'Administrador';
    const initial = (user?.first_name || user?.username || 'U').charAt(0).toUpperCase();

    return (
        <>
            <link
                rel="stylesheet"
                href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.3/font/bootstrap-icons.min.css"
            />

            <aside className={`saio-sidebar ${isCollapsed ? 'is-collapsed' : ''}`}>
                {/* Marca */}
                <div className="saio-sidebar__brand">
                    <Link to="/" className="saio-sidebar__logo" title="SAIO SUSHI">
                        <img src={logo} alt="SAIO SUSHI" />
                    </Link>
                    <div className="saio-sidebar__wordmark">
                        <span className="saio-sidebar__name">SAIO</span>
                        <span className="saio-sidebar__tag">Puré sushi experience</span>
                    </div>
                    <button
                        type="button"
                        className="saio-sidebar__toggle"
                        onClick={() => setIsCollapsed((v) => !v)}
                        title={isCollapsed ? 'Expandir menú' : 'Contraer menú'}
                        aria-label={isCollapsed ? 'Expandir menú' : 'Contraer menú'}
                    >
                        <i className={`bi ${isCollapsed ? 'bi-chevron-right' : 'bi-chevron-left'}`}></i>
                    </button>
                </div>

                {/* Navegación */}
                <nav className="saio-sidebar__nav">
                    {sections.map((section) => (
                        <div key={section.title} className="saio-sidebar__section">
                            <div className="saio-sidebar__section-title">{section.title}</div>
                            <ul>
                                {section.items.map((item) => {
                                    const active = isActive(item);
                                    return (
                                        <li key={item.path}>
                                            <Link
                                                to={item.path}
                                                className={`saio-sidebar__link ${active ? 'is-active' : ''}`}
                                                title={isCollapsed ? item.label : undefined}
                                                aria-current={active ? 'page' : undefined}
                                            >
                                                <i className={`bi ${item.icon}`}></i>
                                                <span>{item.label}</span>
                                            </Link>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    ))}
                </nav>

                {/* Usuario + salir */}
                <div className="saio-sidebar__foot">
                    {!isCollapsed && <PrinterStatusIndicator />}
                    <div className="saio-sidebar__user">
                        <div className="saio-sidebar__avatar">{initial}</div>
                        <div className="saio-sidebar__user-text">
                            <div className="saio-sidebar__user-name">{displayName}</div>
                            <div className="saio-sidebar__user-role">{roleLabel}</div>
                        </div>
                        <button
                            type="button"
                            className="saio-sidebar__logout"
                            onClick={logout}
                            title="Cerrar sesión"
                            aria-label="Cerrar sesión"
                        >
                            <i className="bi bi-box-arrow-right"></i>
                        </button>
                    </div>
                </div>
            </aside>

            <style>{`
                .saio-sidebar {
                    --sb-bg: #f2ede4;
                    --sb-bg-2: #f7f3ec;
                    --sb-line: rgba(74,50,34,0.10);
                    --sb-text: #5a4a3e;
                    --sb-text-strong: #2b211b;
                    --sb-muted: #a89c8c;
                    --sb-amber: #c9973a;

                    position: fixed;
                    top: 0;
                    left: 0;
                    height: 100vh;
                    width: ${SIDEBAR_WIDTH}px;
                    display: flex;
                    flex-direction: column;
                    background: linear-gradient(180deg, var(--sb-bg-2) 0%, var(--sb-bg) 100%);
                    color: var(--sb-text);
                    border-right: 1px solid var(--sb-line);
                    box-shadow: 8px 0 32px -20px rgba(74,50,34,0.18);
                    z-index: 1000;
                    transition: width 0.28s cubic-bezier(.2,.7,.2,1);
                    overflow: hidden;
                }
                .saio-sidebar.is-collapsed { width: ${SIDEBAR_COLLAPSED}px; }

                /* --- Marca --- */
                .saio-sidebar__brand {
                    display: flex;
                    align-items: center;
                    gap: 0.75rem;
                    padding: 1.1rem 1rem 1rem;
                    border-bottom: 1px solid var(--sb-line);
                    min-height: 76px;
                }
                .saio-sidebar__logo {
                    flex-shrink: 0;
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    overflow: hidden;
                    box-shadow: 0 0 0 1px rgba(201,151,58,0.45), 0 8px 18px -10px rgba(74,50,34,0.45);
                    display: block;
                }
                .saio-sidebar__logo img { width: 100%; height: 100%; object-fit: cover; display: block; }
                .saio-sidebar__wordmark {
                    flex: 1;
                    min-width: 0;
                    display: flex;
                    flex-direction: column;
                    line-height: 1.1;
                    opacity: 1;
                    transition: opacity 0.2s ease;
                }
                .saio-sidebar__name {
                    font-size: 1.2rem;
                    font-weight: 700;
                    letter-spacing: 0.14em;
                    color: var(--sb-text-strong);
                }
                .saio-sidebar__tag {
                    font-size: 0.56rem;
                    letter-spacing: 0.24em;
                    text-transform: uppercase;
                    color: var(--sb-amber);
                    margin-top: 3px;
                    white-space: nowrap;
                }
                .saio-sidebar__toggle {
                    flex-shrink: 0;
                    width: 28px;
                    height: 28px;
                    border-radius: 6px;
                    border: 1px solid var(--sb-line);
                    background: transparent;
                    color: var(--sb-muted);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.8rem;
                    transition: background 0.15s, color 0.15s;
                }
                .saio-sidebar__toggle:hover { background: rgba(74,50,34,0.06); color: var(--sb-text-strong); }
                .saio-sidebar.is-collapsed .saio-sidebar__brand { flex-direction: column; gap: 0.6rem; padding: 0.9rem 0.5rem; }
                .saio-sidebar.is-collapsed .saio-sidebar__wordmark { display: none; }

                /* --- Navegación --- */
                .saio-sidebar__nav {
                    flex: 1;
                    overflow-y: auto;
                    overflow-x: hidden;
                    padding: 0.75rem 0.6rem 1rem;
                    scrollbar-width: thin;
                    scrollbar-color: rgba(74,50,34,0.18) transparent;
                }
                .saio-sidebar__nav::-webkit-scrollbar { width: 5px; }
                .saio-sidebar__nav::-webkit-scrollbar-thumb { background: rgba(74,50,34,0.18); border-radius: 5px; }
                .saio-sidebar__section + .saio-sidebar__section { margin-top: 0.9rem; }
                .saio-sidebar__section-title {
                    font-size: 0.6rem;
                    letter-spacing: 0.26em;
                    text-transform: uppercase;
                    color: var(--sb-muted);
                    padding: 0 0.75rem;
                    margin-bottom: 0.4rem;
                    white-space: nowrap;
                }
                .saio-sidebar.is-collapsed .saio-sidebar__section-title {
                    font-size: 0;
                    height: 1px;
                    margin: 0.35rem 0.75rem 0.55rem;
                    background: var(--sb-line);
                }
                .saio-sidebar__section ul { list-style: none; margin: 0; padding: 0; }
                .saio-sidebar__link {
                    position: relative;
                    display: flex;
                    align-items: center;
                    gap: 0.8rem;
                    padding: 0.6rem 0.75rem;
                    margin: 1px 0;
                    border-radius: 7px;
                    color: var(--sb-text);
                    text-decoration: none;
                    font-size: 0.875rem;
                    font-weight: 400;
                    letter-spacing: 0.01em;
                    white-space: nowrap;
                    transition: background 0.15s ease, color 0.15s ease;
                }
                .saio-sidebar__link i {
                    font-size: 1.05rem;
                    width: 22px;
                    text-align: center;
                    flex-shrink: 0;
                    color: var(--sb-muted);
                    transition: color 0.15s ease;
                }
                .saio-sidebar__link span {
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .saio-sidebar__link:hover {
                    background: rgba(74,50,34,0.06);
                    color: var(--sb-text-strong);
                }
                .saio-sidebar__link:hover i { color: #4a3222; }
                .saio-sidebar__link.is-active {
                    background: #ffffff;
                    color: var(--sb-text-strong);
                    font-weight: 600;
                    box-shadow: 0 1px 2px rgba(74,50,34,0.08);
                }
                .saio-sidebar__link.is-active i { color: #4a3222; }
                .saio-sidebar__link.is-active::before {
                    content: '';
                    position: absolute;
                    left: -0.6rem;
                    top: 20%;
                    height: 60%;
                    width: 2px;
                    background: var(--sb-amber);
                    border-radius: 0 2px 2px 0;
                }
                .saio-sidebar.is-collapsed .saio-sidebar__link { justify-content: center; padding: 0.65rem 0; }
                .saio-sidebar.is-collapsed .saio-sidebar__link span { display: none; }
                .saio-sidebar.is-collapsed .saio-sidebar__link.is-active::before { left: -0.6rem; }

                /* --- Pie: usuario --- */
                .saio-sidebar__foot {
                    border-top: 1px solid var(--sb-line);
                    padding: 0.75rem 0.6rem 0.9rem;
                    background: rgba(74,50,34,0.03);
                }
                .saio-sidebar__user {
                    display: flex;
                    align-items: center;
                    gap: 0.65rem;
                    padding: 0.4rem 0.4rem 0.4rem 0.45rem;
                    border-radius: 8px;
                }
                .saio-sidebar__avatar {
                    width: 34px;
                    height: 34px;
                    border-radius: 50%;
                    flex-shrink: 0;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1rem;
                    font-weight: 700;
                    color: #ffffff;
                    background: linear-gradient(135deg, #6b4a33, #4a3222);
                }
                .saio-sidebar__user-text { flex: 1; min-width: 0; line-height: 1.2; }
                .saio-sidebar__user-name {
                    font-size: 0.85rem;
                    font-weight: 500;
                    color: var(--sb-text-strong);
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .saio-sidebar__user-role {
                    font-size: 0.62rem;
                    letter-spacing: 0.14em;
                    text-transform: uppercase;
                    color: var(--sb-muted);
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    margin-top: 2px;
                }
                .saio-sidebar__logout {
                    flex-shrink: 0;
                    width: 32px;
                    height: 32px;
                    border-radius: 6px;
                    border: 1px solid transparent;
                    background: transparent;
                    color: var(--sb-muted);
                    font-size: 1rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.15s ease;
                }
                .saio-sidebar__logout:hover {
                    color: #b04a3d;
                    background: rgba(176,74,61,0.08);
                    border-color: rgba(176,74,61,0.25);
                }
                .saio-sidebar.is-collapsed .saio-sidebar__user { flex-direction: column; gap: 0.5rem; padding: 0.4rem 0; }
                .saio-sidebar.is-collapsed .saio-sidebar__user-text { display: none; }

                /* --- Contenido principal se desplaza --- */
                .main-content {
                    margin-left: ${isCollapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH}px;
                    transition: margin-left 0.28s cubic-bezier(.2,.7,.2,1);
                    min-height: 100vh;
                }

                @media (max-width: 640px) {
                    .saio-sidebar { transform: ${isCollapsed ? 'translateX(-100%)' : 'translateX(0)'}; width: ${SIDEBAR_WIDTH}px; }
                    .saio-sidebar.is-collapsed .saio-sidebar__wordmark { display: flex; }
                    .main-content { margin-left: 0 !important; }
                }
            `}</style>
        </>
    );
};

export default BarraLateral;
